import { useCookieConsentStore } from "~/layers/base/stores/cookieConsentStore";
import {
	parseCookieYesConsent,
	readCookieYesCookie,
	type CookieYesCategories
} from "~/layers/base/utils/cookieyesConsent";

type CookieYesWindow = Window & {
	getCkyConsent?: () => {
		categories?: Record<string, boolean>;
		isUserActionCompleted?: boolean;
	};
};

/**
 * CookieYes → base consent store bridge. Active only when
 * `NORCE_CMP=cookieyes`; inert for every other template.
 *
 * With an external CMP the base Banner never renders, so the store's
 * marketing/statistics/preferences flags would stay false forever and
 * `useConsentCategory()`-gated code (HelloRetail, Dotdigital)
 * would never start. Mirrors CookieYes' decision so `useCookieConsent()`
 * stays the single source of truth (AGENTS.md rule #4).
 *
 * State only: CookieYes owns its own cookie, and its GTM template already
 * drives Google Consent Mode — `syncFromCmp()` re-dispatches neither.
 */
export default defineNuxtPlugin((nuxtApp) => {
	if (useRuntimeConfig().public.cmp !== "cookieyes") return;

	const w = window as CookieYesWindow;

	const readCategories = (): CookieYesCategories | null => {
		const fromCookie = parseCookieYesConsent(
			readCookieYesCookie(document.cookie)
		);
		if (fromCookie) return fromCookie;

		// Fallback for when the cookie is missing: it can lag the event by a tick,
		// and CookieYes scopes it to the registered domain, so it never lands on a
		// test subdomain. Gated on isUserActionCompleted — without it we would
		// record "rejected" before the visitor has chosen anything.
		const consent = w.getCkyConsent?.();
		if (!consent?.isUserActionCompleted || !consent.categories) return null;
		return {
			statistics: Boolean(consent.categories.analytics),
			marketing: Boolean(consent.categories.advertisement),
			preferences: Boolean(consent.categories.functional)
		};
	};

	// Deferred to app:mounted for the same reason as cookieConsent.client.ts —
	// SSR HTML is cached and must not depend on a consent cookie.
	nuxtApp.hook("app:mounted", () => {
		const store = useCookieConsentStore();

		const sync = () => {
			const categories = readCategories();
			if (categories) store.syncFromCmp(categories);
		};

		document.addEventListener("cookieyes_consent_update", sync);
		sync();

		if (import.meta.hot) {
			import.meta.hot.dispose(() => {
				document.removeEventListener("cookieyes_consent_update", sync);
			});
		}
	});
});
